import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { DataGridFilterInput } from "./DataGridFilterInput";
import { DataGridColumnConfig, DataGridFilterType } from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGridFilterRowHeader } from "./DataGridFilterRow";
import { Button } from "components/Button";
import { useFilter } from "./useFilter";

export const DataGridFilterModal: React.FC<{
    className?: string;
    filter?: DataGridFilterType;
    columns: DataGridColumnConfig[];
    filterSelected: string;
    isFilterOpen: boolean;
    isFilterEmpty: boolean;
    getFilterValue: ReturnType<typeof useFilter>["getFilterValue"];
    toggleFilter: (state?: boolean) => void;
    toggleSort: (state?: boolean) => void;
    onFilterChange: (filter: string) => void;
    onFilterValueKeyDown: (key: string, event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onFilterSubmit: (filterValue?: DataGridFilterType) => void;
    onFieldClean: (key: string) => void;
    onFilterInputFocus: (key: string) => void;
}> = ({
    className,
    filter,
    columns,
    filterSelected,
    isFilterOpen,
    isFilterEmpty,
    getFilterValue,
    toggleFilter,
    toggleSort,
    onFilterValueKeyDown,
    onFilterSubmit,
    onFieldClean,
    onFilterInputFocus,
}) => {
    const column = useMemo(
        () =>
            columns
                .map((c) => ({
                    ...c,
                    value: getFilterValue(c.key),
                }))
                .find((c) => c.key === filterSelected),
        [columns, getFilterValue, filterSelected]
    );

    const [value, setValue] = useState<string>(column?.value ?? "");
    const [previousFilterSelected, setPreviousFilterSelected] = useState(filterSelected);

    if (!filterSelected || !column) return;

    if (previousFilterSelected !== filterSelected) {
        setValue(column.value);
        setPreviousFilterSelected(filterSelected);
    }

    const onApply = (event: React.MouseEvent) => {
        event.preventDefault();

        onFilterSubmit({ ...filter, [column.key]: value });
        toggleFilter(false);
    };

    const onFilterClean = () => {
        setValue("");
        onFieldClean(column.key);
    };

    return (
        <Modal
            show={isFilterOpen}
            onHide={toggleFilter}
            aria-labelledby="data-grid-filter-modal"
            className={`d-table-row ${className}`}
            dialogClassName="m-0"
            contentClassName="rounded-0"
        >
            <Modal.Header className="rounded-0 border border-top-0  border-end-0 border-start-0 align-items-center">
                <DataGridFilterRowHeader
                    className="d-flex flex-shrink-0 gap-2"
                    columns={columns}
                    filter={filter}
                    filterSelected={filterSelected}
                    allowActiveAll={false}
                    isFilterEmpty={isFilterEmpty}
                    toggleFilter={toggleFilter}
                    toggleSort={toggleSort}
                    onFilterInputFocus={onFilterInputFocus}
                />
            </Modal.Header>
            <Modal.Body>
                <div className="text-capitalize">{column.name}</div>
                <DataGridFilterInput
                    column={column}
                    value={value}
                    showPlaceholder={true}
                    onChange={setValue}
                    onKeyDown={onFilterValueKeyDown}
                />
                <p style={{ color: "#706B85" }} className="py-3 d-flex align-items-center">
                    <FontAwesomeIcon aria-label="done" className="pe-2" icon={["fal", "circle-info"]} />
                    Use * as a wildcard in your search to find potential matches (e.g., ABC123*).
                </p>
                <Modal.Footer className="d-flex justify-content-between p-0 m-0">
                    <Button
                        style={{
                            color: "initial",
                            borderColor: "var(--bg-active) !important",
                        }}
                        className="bg-transparent border"
                        onClick={onFilterClean}
                    >
                        Clear
                    </Button>
                    <Button variant="primary" onClick={onApply}>
                        Apply
                    </Button>
                </Modal.Footer>
            </Modal.Body>
            <hr className="m-0" />
        </Modal>
    );
};
